import React, { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Checkbox, Typography, Input, Button, Switch } from '@material-tailwind/react'
import { getEntityConfig } from '@/constants/permission-entities'
import {
  useAddPermissionMutation,
  useRemovePermissionMutation
} from '@/store/services/user-groups.service'

const ModelPermissionAssignment = ({
  groupId,
  permissibleType,
  title,
  useGetItemsQuery,
  dataKey
}) => {
  const { encoded, nameField, descriptionField } = getEntityConfig(permissibleType)

  const [addPermission] = useAddPermissionMutation()
  const [removePermission] = useRemovePermissionMutation()

  const { data, isLoading } = useGetItemsQuery('?all=true')
  const items = data?.[dataKey] || []

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedItems, setSelectedItems] = useState([])
  const [alwaysAllSelected, setAlwaysAllSelected] = useState(false)

  const filteredItems = useMemo(() => {
    const searchLower = searchTerm.toLowerCase()
    return items.filter(item => {
      const nameMatch = item[nameField]?.toLowerCase().includes(searchLower)
      const descMatch = descriptionField && item[descriptionField]?.toLowerCase().includes(searchLower)
      return nameMatch || descMatch
    })
  }, [items, searchTerm, nameField, descriptionField])

  const allSelected = useMemo(() =>
    filteredItems.length > 0 &&
    filteredItems.every(item => alwaysAllSelected || selectedItems.includes(item.id)),
  [filteredItems, selectedItems, alwaysAllSelected]
  )

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const response = await fetch(
          `/api/v1/groups/${groupId}/permissions?permissible_type=${encoded}`
        )
        const data = await response.json()
        setSelectedItems(data.permissible_ids || [])
        setAlwaysAllSelected(Boolean(data.all_selected))
      } catch (error) {
        console.error('Error cargando permisos:', error)
      }
    }

    groupId && loadPermissions()
  }, [groupId, encoded])

  const handleItemToggle = async (itemId) => {
    if (alwaysAllSelected) return

    const isSelected = selectedItems.includes(itemId)

    try {
      await (isSelected ? removePermission : addPermission)({
        groupId,
        permissible_type: encoded,
        permissible_ids: [itemId]
      }).unwrap()

      setSelectedItems(prev =>
        isSelected
          ? prev.filter(id => id !== itemId)
          : [...prev, itemId]
      )
    } catch (error) {
      console.error('Error actualizando permiso:', error)
    }
  }

  const handleSelectAll = async () => {
    if (alwaysAllSelected) return

    const ids = filteredItems.map(item => item.id)
    const mutation = allSelected ? removePermission : addPermission

    try {
      await mutation({
        groupId,
        permissible_type: encoded,
        permissible_ids: ids
      }).unwrap()

      setSelectedItems(prev =>
        allSelected
          ? prev.filter(id => !ids.includes(id))
          : [...new Set([...prev, ...ids])]
      )
    } catch (error) {
      console.error('Error actualizando permisos:', error)
    }
  }

  const handleSelectNone = async () => {
    if (alwaysAllSelected) return

    const ids = filteredItems.map(item => item.id)

    try {
      await removePermission({
        groupId,
        permissible_type: encoded,
        permissible_ids: ids
      }).unwrap()

      setSelectedItems(prev => prev.filter(id => !ids.includes(id)))
    } catch (error) {
      console.error('Error eliminando permisos:', error)
    }
  }

  const handleAlwaysAllToggle = async () => {
    const nextValue = !alwaysAllSelected
    const mutation = nextValue ? addPermission : removePermission

    try {
      await mutation({
        groupId,
        permissible_type: encoded,
        all_selected: true
      }).unwrap()

      setAlwaysAllSelected(nextValue)
      setSelectedItems([])
    } catch (error) {
      console.error('Error actualizando permisos globales:', error)
    }
  }

  return (
    <div className="p-4 bg-white rounded-lg shadow-sm h-[532px] flex flex-col">
      <div className="mb-4 space-y-4">
        <div className="flex justify-between items-center">
          <Typography variant="h5">{title}</Typography>
          <div className="flex items-center gap-2">
            <Button
              variant="outlined"
              size="sm"
              onClick={handleSelectAll}
              disabled={alwaysAllSelected || filteredItems.length === 0}
              className="border-gray-900 text-gray-900 hover:bg-gray-50 whitespace-nowrap"
            >
              {allSelected ? 'Deseleccionar Todos' : 'Seleccionar Todos'}
            </Button>
            <Button
              variant="outlined"
              size="sm"
              onClick={handleSelectNone}
              disabled={alwaysAllSelected || filteredItems.length === 0 || selectedItems.length === 0}
              className="border-red-500 text-red-500 hover:bg-red-50 whitespace-nowrap"
            >
              Seleccionar Ninguno
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-4 rounded border border-blue-gray-100 bg-blue-gray-50/40 px-3 py-2">
          <div>
            <Typography variant="small" className="font-semibold text-blue-gray-900">
              Siempre todas las entidades
            </Typography>
            <Typography variant="small" className="text-blue-gray-600">
              Incluye nuevas entidades automáticamente.
            </Typography>
          </div>
          <Switch
            checked={alwaysAllSelected}
            onChange={handleAlwaysAllToggle}
            color="blue"
          />
        </div>

        <Input
          label="Buscar..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          icon={<i className="fas fa-search" />}
        />
      </div>

      {isLoading
        ? (
        <div className="space-y-2 flex-1">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-14 bg-gray-100 rounded animate-pulse"></div>
          ))}
        </div>
          )
        : (
        <div className="grid grid-cols-1 gap-2 flex-1 overflow-y-auto">
          {filteredItems.map(item => (
            <div key={item.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded h-14">
              <Checkbox
                checked={alwaysAllSelected || selectedItems.includes(item.id)}
                onChange={() => handleItemToggle(item.id)}
                disabled={alwaysAllSelected}
                className="p-1"
              />
              <div className="flex-1 min-w-0">
                <Typography variant="small" className="font-medium truncate">
                  {item[nameField]}
                </Typography>
                {descriptionField && (
                  <Typography variant="small" color="gray" className="truncate">
                    {item[descriptionField]}
                  </Typography>
                )}
              </div>
            </div>
          ))}

          {filteredItems.length === 0 && (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              {searchTerm ? `No hay resultados para "${searchTerm}"` : 'No hay elementos disponibles'}
            </div>
          )}
        </div>
          )}
    </div>
  )
}

ModelPermissionAssignment.propTypes = {
  groupId: PropTypes.string.isRequired,
  permissibleType: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  useGetItemsQuery: PropTypes.func.isRequired,
  dataKey: PropTypes.string.isRequired
}

export default ModelPermissionAssignment
